import { useEffect, useMemo, useState } from 'react';
import { GamificationPermissions } from '@/constants/permissions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Filter,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useHasPermission } from '@/hooks/use-permission';
import { type PagedResult } from '@/services/base/base.service';
import {
  referralService,
  type AdminReferral,
  type ReferralPlatformStats,
  type ReferralStatus,
  type UpdateReferralStatusInput,
} from '@/services/referral.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';

type DateRangePreset = '7' | '30' | '90' | 'all';
type SortOption = 'newest' | 'oldest' | 'highestRisk';

interface ReferralFilters {
  status: ReferralStatus | 'ALL';
  search: string;
  minFraudScore: number;
  flaggedOnly: boolean;
  dateRange: DateRangePreset;
  sortOption: SortOption;
}

const STATUS_OPTIONS: ReferralStatus[] = [
  'Pending',
  'Active',
  'Expired',
  'Cancelled',
];
const DATE_PRESETS: Array<{ label: string; value: DateRangePreset }> = [
  { label: '7d', value: '7' },
  { label: '30d', value: '30' },
  { label: '90d', value: '90' },
  { label: 'All', value: 'all' },
];
const SORTING_MAP: Record<SortOption, string> = {
  newest: 'CreationTime DESC',
  oldest: 'CreationTime ASC',
  highestRisk: 'FraudScore DESC',
};
const PAGE_SIZE = 25;

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const statusStyles: Record<ReferralStatus, string> = {
  Pending: 'bg-amber-100 text-amber-800 border border-amber-200',
  Active: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  Expired: 'bg-muted text-muted-foreground border border-border',
  Cancelled: 'bg-rose-100 text-rose-800 border border-rose-200',
};

function formatPercent(value?: number): string {
  if (value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return `${(value * 100).toFixed(1)}%`;
}

function formatDate(value?: string): string {
  if (!value) return '—';
  try {
    return dateFormatter.format(new Date(value));
  } catch {
    return value;
  }
}

function getFraudSeverity(score: number) {
  if (score >= 0.75) {
    return {
      label: 'High Risk',
      className: 'text-rose-600',
      bar: 'bg-rose-500',
    } as const;
  }

  if (score >= 0.45) {
    return {
      label: 'Elevated',
      className: 'text-amber-600',
      bar: 'bg-amber-500',
    } as const;
  }

  return {
    label: 'Low Risk',
    className: 'text-emerald-600',
    bar: 'bg-emerald-500',
  } as const;
}

function getRangeForPreset(preset: DateRangePreset) {
  if (preset === 'all') {
    return { start: undefined, end: undefined };
  }

  const days = parseInt(preset, 10);
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));

  return { start: start.toISOString(), end: end.toISOString() };
}

interface UpdateReferralPayload {
  id: string;
  data: UpdateReferralStatusInput;
}

export function ReferralManagementPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<ReferralFilters>({
    status: 'ALL',
    search: '',
    minFraudScore: 0.2,
    flaggedOnly: false,
    dateRange: '30',
    sortOption: 'newest',
  });
  const [selectedReferral, setSelectedReferral] =
    useState<AdminReferral | null>(null);
  const [statusForm, setStatusForm] = useState({
    status: 'Pending' as ReferralStatus,
    cancelledReason: '',
    adminNotes: '',
    fraudScore: '',
  });

  const { hasPermission: canView, isLoading: permissionsLoading } =
    useHasPermission(GamificationPermissions.Referrals.View);
  const { hasPermission: canEdit } = useHasPermission(
    GamificationPermissions.Referrals.Edit,
  );
  const { hasPermission: canViewStats } = useHasPermission(
    GamificationPermissions.Referrals.ViewStats,
  );

  const { start, end } = useMemo(
    () => getRangeForPreset(filters.dateRange),
    [filters.dateRange],
  );

  const statsQuery = useQuery<ReferralPlatformStats>({
    queryKey: ['referral-platform-stats'],
    queryFn: () => referralService.getPlatformStats(),
    enabled: !permissionsLoading && canViewStats,
  });

  const referralsQuery = useQuery<PagedResult<AdminReferral>>({
    queryKey: [
      'admin-referrals',
      filters.status,
      filters.search,
      filters.minFraudScore,
      filters.flaggedOnly,
      filters.sortOption,
      start,
      end,
      page,
    ],
    queryFn: () =>
      referralService.getReferrals({
        status: filters.status === 'ALL' ? undefined : filters.status,
        search: filters.search.trim() || undefined,
        minFraudScore: filters.minFraudScore,
        flaggedOnly: filters.flaggedOnly,
        startDate: start,
        endDate: end,
        sorting: SORTING_MAP[filters.sortOption],
        skipCount: (page - 1) * PAGE_SIZE,
        maxResultCount: PAGE_SIZE,
      }),
    enabled: !permissionsLoading && canView,
    placeholderData: (previousData) => previousData,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, data }: UpdateReferralPayload) =>
      referralService.updateReferralStatus(id, data),
    onSuccess: (updated) => {
      toast.success('Referral updated');
      queryClient.invalidateQueries({ queryKey: ['admin-referrals'] });
      queryClient.invalidateQueries({ queryKey: ['referral-platform-stats'] });
      setSelectedReferral(updated);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to update referral';
      toast.error(message);
    },
  });

  useEffect(() => {
    if (!selectedReferral) {
      return;
    }

    setStatusForm({
      status: selectedReferral.status,
      cancelledReason: selectedReferral.cancelledReason || '',
      adminNotes: selectedReferral.adminNotes || '',
      fraudScore:
        typeof selectedReferral.fraudScore === 'number'
          ? selectedReferral.fraudScore.toFixed(2)
          : '',
    });
  }, [selectedReferral]);

  const handleFilterChange = (partial: Partial<ReferralFilters>) => {
    setFilters((prev) => ({ ...prev, ...partial }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'ALL',
      search: '',
      minFraudScore: 0.2,
      flaggedOnly: false,
      dateRange: '30',
      sortOption: 'newest',
    });
    setPage(1);
  };

  const handleUpdateStatus = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedReferral) {
      return;
    }

    const payload: UpdateReferralStatusInput = {
      status: statusForm.status,
      adminNotes: statusForm.adminNotes?.trim() || undefined,
    };

    if (statusForm.status === 'Cancelled') {
      payload.cancelledReason = statusForm.cancelledReason?.trim() || undefined;
    }

    const parsedScore = parseFloat(statusForm.fraudScore);
    if (!Number.isNaN(parsedScore)) {
      payload.fraudScore = parsedScore;
    }

    updateStatusMutation.mutate({ id: selectedReferral.id, data: payload });
  };

  if (permissionsLoading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-10 flex items-center gap-3">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span>Checking permissions…</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>Access Restricted</CardTitle>
          </CardHeader>
          <CardContent>
            You do not have permission to view referral analytics. Please ask an
            administrator to grant{' '}
            <code className="mx-1 rounded bg-muted px-1 py-0.5">
              {GamificationPermissions.Referrals.View}
            </code>
            if you believe this is a mistake.
          </CardContent>
        </Card>
      </div>
    );
  }

  const stats = statsQuery.data;
  const referrals = referralsQuery.data;
  const totalPages = referrals?.totalCount
    ? Math.max(1, Math.ceil(referrals.totalCount / PAGE_SIZE))
    : 1;

  const summaryCards = buildSummaryCards(stats);

  return (
    <div className="container mx-auto space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Referral System</h1>
          <p className="text-muted-foreground">
            Monitor referral performance, fraud signals, and admin actions in
            real-time.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => referralsQuery.refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button variant="secondary" onClick={handleClearFilters}>
            <Filter className="mr-2 h-4 w-4" /> Reset Filters
          </Button>
        </div>
      </div>

      {canViewStats && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {statsQuery.isLoading
            ? summaryCards.map((card) => (
                <Card key={card.label}>
                  <CardContent className="space-y-3 p-6">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-3 w-20" />
                  </CardContent>
                </Card>
              ))
            : summaryCards.map((card) => (
                <Card key={card.label}>
                  <CardContent className="space-y-2 p-6">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <card.icon className="h-4 w-4" />
                      {card.label}
                    </div>
                    <p className="text-2xl font-bold">{card.value}</p>
                    <p className="text-xs text-muted-foreground">
                      {card.subtext}
                    </p>
                  </CardContent>
                </Card>
              ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="h-4 w-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Referrer, referred user, email, code"
                  value={filters.search}
                  onChange={(event) =>
                    handleFilterChange({ search: event.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={filters.status}
                onValueChange={(value) =>
                  handleFilterChange({
                    status: value as ReferralFilters['status'],
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All statuses</SelectItem>
                  {STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Sort</label>
              <Select
                value={filters.sortOption}
                onValueChange={(value: SortOption) =>
                  handleFilterChange({ sortOption: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="highestRisk">Highest risk</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">
                  Minimum fraud score
                </label>
                <span className="text-xs text-muted-foreground">
                  {(filters.minFraudScore * 100).toFixed(0)}%
                </span>
              </div>
              <Slider
                value={[filters.minFraudScore]}
                min={0}
                max={1}
                step={0.05}
                onValueChange={([value]) =>
                  handleFilterChange({ minFraudScore: value })
                }
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="text-sm font-medium">
                  Show flagged referrals only
                </p>
                <p className="text-xs text-muted-foreground">
                  Requires fraud score above the selected threshold.
                </p>
              </div>
              <Switch
                checked={filters.flaggedOnly}
                onCheckedChange={(checked) =>
                  handleFilterChange({ flaggedOnly: checked })
                }
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Date range</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.value}
                  type="button"
                  size="sm"
                  variant={
                    filters.dateRange === preset.value ? 'primary' : 'outline'
                  }
                  onClick={() =>
                    handleFilterChange({ dateRange: preset.value })
                  }
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-base">
              <span>Latest referrals</span>
              <Badge variant="outline">
                {referrals?.totalCount ?? 0} records
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {referralsQuery.isLoading ? (
              <div className="space-y-2 p-6">
                {[...Array(6)].map((_, index) => (
                  <Skeleton key={index} className="h-12 w-full" />
                ))}
              </div>
            ) : referrals?.items?.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Referrer</TableHead>
                    <TableHead>Referred</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Fraud score</TableHead>
                    <TableHead>Rewards</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrals.items.map((referral) => {
                    const fraud = getFraudSeverity(referral.fraudScore);
                    return (
                      <TableRow key={referral.id}>
                        <TableCell>
                          <p className="font-medium">{referral.referrerName}</p>
                          <p className="text-xs text-muted-foreground">
                            {referral.referrerEmail}
                          </p>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{referral.referredName}</p>
                          <p className="text-xs text-muted-foreground">
                            {referral.referredEmail}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusStyles[referral.status]}>
                            {referral.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-semibold">
                            <span className={fraud.className}>
                              {referral.fraudScore.toFixed(2)}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {fraud.label}
                          </p>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-semibold text-emerald-600">
                            +{referral.referrerCoinsEarned} coins
                          </div>
                          <p className="text-xs text-muted-foreground">
                            +{referral.referrerTSparksEarned} T-Sparks
                          </p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">
                            {formatDate(referral.creationTime)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {referral.referralCode || '—'}
                          </p>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedReferral(referral)}
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="p-10 text-center text-muted-foreground">
                No referrals found for the selected filters.
              </div>
            )}
          </CardContent>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t p-4 text-sm">
              <span>
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() =>
                    setPage((current) => Math.min(totalPages, current + 1))
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4" /> Top referrers
              </CardTitle>
            </CardHeader>
            <CardContent>
              {statsQuery.isLoading ? (
                <div className="space-y-2">
                  {[...Array(4)].map((_, index) => (
                    <Skeleton key={index} className="h-12 w-full" />
                  ))}
                </div>
              ) : stats?.topReferrers?.length ? (
                <div className="space-y-4">
                  {stats.topReferrers.slice(0, 5).map((referrer) => (
                    <div
                      key={referrer.userId}
                      className="flex items-center justify-between"
                    >
                      <div>
                        <p className="font-medium">{referrer.userName}</p>
                        <p className="text-xs text-muted-foreground">
                          {referrer.successfulReferrals} successful referrals
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-emerald-600">
                        +{referrer.totalRewards} coins
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No leaderboard data yet.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldAlert className="h-4 w-4" /> Fraud signals
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.fraudFlagCounts &&
              Object.keys(stats.fraudFlagCounts).length ? (
                <div className="space-y-3">
                  {Object.entries(stats.fraudFlagCounts)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 6)
                    .map(([flag, count]) => (
                      <div
                        key={flag}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm capitalize">{flag}</span>
                        <Badge variant="outline">{count}</Badge>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Fraud flag distribution will appear once the system detects
                  risky patterns.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog
        open={!!selectedReferral}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedReferral(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          {selectedReferral && (
            <form onSubmit={handleUpdateStatus} className="space-y-6">
              <DialogHeader>
                <DialogTitle>Referral Review</DialogTitle>
              </DialogHeader>

              <div className="grid gap-4 md:grid-cols-2">
                <InfoCard
                  title="Referrer"
                  name={selectedReferral.referrerName}
                  email={selectedReferral.referrerEmail}
                  userId={selectedReferral.referrerId}
                />
                <InfoCard
                  title="Referred User"
                  name={selectedReferral.referredName}
                  email={selectedReferral.referredEmail}
                  userId={selectedReferral.referredId}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Fraud insights</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div>
                      <p className="text-muted-foreground">Fraud score</p>
                      <p className="text-xl font-semibold">
                        {selectedReferral.fraudScore.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Flags</p>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {selectedReferral.fraudFlags?.length ? (
                          selectedReferral.fraudFlags.map((flag) => (
                            <Badge key={flag} variant="outline">
                              {flag}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            No flags recorded
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Technical signals</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <p className="text-muted-foreground">IP Address</p>
                    <p>{selectedReferral.ipAddress || '—'}</p>
                    <p className="text-muted-foreground">Device ID</p>
                    <p className="break-all">
                      {selectedReferral.deviceId || '—'}
                    </p>
                    <p className="text-muted-foreground">User Agent</p>
                    <ScrollArea className="h-16 rounded border p-2 text-xs text-muted-foreground">
                      {selectedReferral.userAgent || '—'}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              {canEdit ? (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1">
                      <label className="text-sm font-medium">Status</label>
                      <Select
                        value={statusForm.status}
                        onValueChange={(value) =>
                          setStatusForm((prev) => ({
                            ...prev,
                            status: value as ReferralStatus,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-medium">
                        Override fraud score
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        max={1}
                        value={statusForm.fraudScore}
                        onChange={(event) =>
                          setStatusForm((prev) => ({
                            ...prev,
                            fraudScore: event.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  {statusForm.status === 'Cancelled' && (
                    <div className="space-y-1">
                      <label className="text-sm font-medium">
                        Cancellation reason
                      </label>
                      <Textarea
                        value={statusForm.cancelledReason}
                        onChange={(event) =>
                          setStatusForm((prev) => ({
                            ...prev,
                            cancelledReason: event.target.value,
                          }))
                        }
                        placeholder="Document why this referral is being cancelled"
                      />
                    </div>
                  )}

                  <div className="space-y-1">
                    <label className="text-sm font-medium">Admin notes</label>
                    <Textarea
                      value={statusForm.adminNotes}
                      onChange={(event) =>
                        setStatusForm((prev) => ({
                          ...prev,
                          adminNotes: event.target.value,
                        }))
                      }
                      placeholder="Internal notes about this review"
                    />
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border bg-muted/30 p-4 text-sm">
                  <p className="font-medium">Read only</p>
                  <p className="text-muted-foreground">
                    You need{' '}
                    <code className="mx-1 rounded bg-muted px-1 py-0.5">
                      {GamificationPermissions.Referrals.Edit}
                    </code>
                    to update referral statuses.
                  </p>
                </div>
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelectedReferral(null)}
                >
                  Close
                </Button>
                {canEdit && (
                  <Button
                    type="submit"
                    disabled={updateStatusMutation.isPending}
                  >
                    {updateStatusMutation.isPending
                      ? 'Saving…'
                      : 'Save changes'}
                  </Button>
                )}
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface InfoCardProps {
  title: string;
  name: string;
  email: string;
  userId: string;
}

function InfoCard({ title, name, email, userId }: InfoCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        <p className="font-medium">{name}</p>
        <p className="text-muted-foreground">{email}</p>
        <p className="text-xs text-muted-foreground">ID: {userId}</p>
      </CardContent>
    </Card>
  );
}

interface SummaryCardConfig {
  label: string;
  value: string;
  subtext: string;
  icon: typeof Users;
}

function buildSummaryCards(stats?: ReferralPlatformStats): SummaryCardConfig[] {
  return [
    {
      label: 'Total referrals',
      value: (stats?.totalReferrals ?? 0).toLocaleString(),
      subtext: `${stats?.activeReferrals ?? 0} active · ${stats?.pendingReferrals ?? 0} pending`,
      icon: Users,
    },
    {
      label: 'Conversion rate',
      value: formatPercent(stats?.conversionRate),
      subtext: `${stats?.activeReferrals ?? 0} / ${stats?.totalReferrals ?? 0}`,
      icon: Activity,
    },
    {
      label: 'Fraud ratio',
      value: formatPercent(stats?.fraudRatio),
      subtext: `${stats?.highFraudReferrals ?? 0} high-risk referrals`,
      icon: ShieldAlert,
    },
    {
      label: 'Average fraud score',
      value: stats?.averageFraudScore?.toFixed(2) ?? '—',
      subtext: `${stats?.autoCancelledReferrals ?? 0} auto-cancelled`,
      icon: ShieldCheck,
    },
  ];
}

export default ReferralManagementPage;
